import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { baseUrl } from '../../utils/axios'
import { toast } from 'react-toastify'
import { handleErrors, formatParams, getParams, formatDateForBackend } from 'utils/helpers'
import { AxiosResponse } from 'axios'
import { IBedHistoryPaginationResponse, IPaginatedResponse } from 'types/api/general.interface'
import { IAccommodationModalCamp, IAccommodationSlice } from 'types/accommodation.interface'
import {
  IAccommodationInfoResponse,
  IAccommodationSingleResponse,
  ICheckInPostRequest,
  ICreateCampPostRequest,
  ICreateGuestPostRequest,
  ICreateGuestPostRequestResponse,
  IGetAccommodationStatistics,
  IGetAllAccommodationsSingleResponse,
  IGetAllAccommodationUsersSingleResponse,
  IGetAllAvailableBedsSingleResponse,
  IGetAllBedsHiistorySingleResponse,
  IGetAllCampsSingleResponse,
  IGetAllExpectedCheckoutsSingleResponse,
} from 'types/api/accommodationApi.interface'
import { accommodationCategories, accommodationTypes } from 'pages/Accomodation/accommodationData'
import { IConfirmCheckInModal } from 'pages/Accomodation/ConfirmCheckInModal'

const initialState: IAccommodationSlice = {
  isLoading: false,
  statistics: null,
  accommodations: [],
  singleAccommodationInfo: null,
  totalCount: 0,
  totalPages: 0,
  bedsHistory: {
    data: [],
    totalCount: 0,
    totalPages: 0,
    overdueCount: 0,
    completedCount: 0,
    activeCount: 0,
  },
  checkOuts: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
  camps: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
  beds: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
  accomodationUsers: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
}

export const getAccommodations = createAsyncThunk(
  'accommodations/getAccommodations',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllAccommodationsSingleResponse>> =
        await baseUrl.get('accommodation/management/accommodation', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getAccommodationUsers = createAsyncThunk(
  'accommodations/getAccommodationUsers',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllAccommodationUsersSingleResponse>> =
        await baseUrl.get('accommodation/management/accommodation/users', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleAccommodationInfo = createAsyncThunk(
  'accommodations/getSingleAccommodationInfo',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IAccommodationInfoResponse> = await baseUrl.get(
        'accommodation/management/accommodation/info/' + id,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getCheckouts = createAsyncThunk(
  'accommodations/getCheckouts',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IGetAllExpectedCheckoutsSingleResponse> = await baseUrl.get(
        'accommodation/management/accommodation/expected-checkout-by-date',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const checkIn = createAsyncThunk(
  'accommodations/checkIn',
  async (state: IConfirmCheckInModal, thunkAPI) => {
    try {
      let guestResp: AxiosResponse<ICreateGuestPostRequestResponse> = null
      if (state.type === 'Guest' && !state.complance) {
        const guestObj: ICreateGuestPostRequest = {
          identificationNumber: state.identificationNumber,
          firstName: state.person.split(' ')[0],
          lastName: state.person.split(' ')[1] || '',
          gender: state.gender?.value == 1 ? 'm' : 'f',
          expectedCheckoutDate: formatDateForBackend(state.checkOut),
        }
        guestResp = await baseUrl.post('accommodation/management/accommodation/guest', guestObj)
      }

      const obj: ICheckInPostRequest = {
        userId: state.type === 'Employee' ? state.userId : null,
        accommodationGuestId: state.type === 'Guest' ? guestResp.data.id : null,
        personType: state.type === 'Employee' ? 1 : 2,
        bedId: state.bedId,
        checkInTimestamp: formatDateForBackend(state.checkIn),
        expectedCheckOutTimestamp: formatDateForBackend(state.checkOut),
        overrideReason: '',
      }
      const resp = await baseUrl.post('accommodation/management/accommodation/check-in', obj)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)
export const checkOut = createAsyncThunk(
  'accommodations/checkOut',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.post(`accommodation/management/accommodation/check-out/${id}`)
      await thunkAPI.dispatch(getCheckouts(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleAccommodation = createAsyncThunk(
  'accommodations/getSingleAccomodation',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IAccommodationSingleResponse> = await baseUrl.get(
        'accommodation/management/accommodation/' + id,
      )
      const state = {
        id: resp.data.id,
        name: resp.data.name,
        address: resp.data.address,
        category: accommodationCategories.find((c) => c.value === resp.data.category),
        type: accommodationTypes.find((t) => t.value === resp.data.type),
        startDate: new Date(resp.data.startDate),
        endDate: new Date(resp.data.endDate),
        rooms: resp.data.rooms,
      }
      return state
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createCamp = createAsyncThunk(
  'accommodations/createCamp',
  async (state: IAccommodationModalCamp, thunkAPI) => {
    const req: ICreateCampPostRequest = {
      name: state.name,
      address: state.address,
      coordinates: {
        latitude: state.coordinates.latitude || 0,
        longitude: state.coordinates.longitude || 0,
      },
      status: state.status?.value,
      blocks: state.blocks.map((block) => ({
        name: block.name,
        status: block.status?.value,
        genderPolicy: block.genderPolicy?.value,
        tierPolicy: block.tierPolicy?.value,
        rooms: block.rooms.map((room) => ({
          label: room.name,
          capacity: room.capacity,
          status: room.status?.value,
          beds: room.beds.map((bed) => ({
            label: bed.name,
            state: bed.state?.value,
          })),
        })),
      })),
    }
    try {
      const resp: AxiosResponse = await baseUrl.post(
        'accommodation/management/accommodation/camp',
        req,
      )
      await thunkAPI.dispatch(getCamps({ location }))
      await thunkAPI.dispatch(getAccommodationStatistics({ location }))
      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getAccommodationStatistics = createAsyncThunk(
  'accommodations/getAccommodationStatistics',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IGetAccommodationStatistics> = await baseUrl.get(
        'accommodation/management/accommodation/statistics',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getCamps = createAsyncThunk(
  'accommodations/getCamps',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllCampsSingleResponse>> = await baseUrl.get(
        'accommodation/management/accommodation/camps',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getBeds = createAsyncThunk('accommodations/getBeds', async (params: any, thunkAPI) => {
  try {
    params = formatParams(params)
    const resp: AxiosResponse<IPaginatedResponse<IGetAllAvailableBedsSingleResponse>> =
      await baseUrl.get('accommodation/management/accommodation/available-beds', { params })
    return { ...resp.data, page: params?.page ?? 1 }
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const getBedsHistory = createAsyncThunk(
  'accommodations/getBedsHistory',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllBedsHiistorySingleResponse>> =
        await baseUrl.get('accommodation/management/accommodation/occupancies', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editAccommodation = createAsyncThunk(
  'accommodations/editAccomodation',
  async (state, thunkAPI) => {
    const req = {}
    try {
      const resp: AxiosResponse<IGetAllAccommodationsSingleResponse> = await baseUrl.patch(
        'accommodation/management/accommodation/' + state,
        req,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteAccommodation = createAsyncThunk(
  'accommodations/deleteAccomodation',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('accommodation/management/accommodation/' + id)
      await thunkAPI.dispatch(getAccommodations(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const accommodationSlice = createSlice({
  name: 'accommodation',
  initialState,
  reducers: {
    clearAccommodations: (state) => {
      state.accommodations = []
    },
    clearAccommodationInfo: (state) => {
      state.singleAccommodationInfo = null
    },
    clearAccommodationUsers: (state) => {
      state.accomodationUsers = {
        data: [],
        totalCount: 0,
        totalPages: 0,
      }
    },
    clearBeds: (state) => {
      state.beds = {
        data: [],
        totalCount: 0,
        totalPages: 0,
      }
    },
  },

  extraReducers: {
    [getAccommodations.pending.type]: (state) => {
      state.isLoading = true
    },
    [getAccommodations.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllAccommodationsSingleResponse> },
    ) => {
      state.accommodations = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
      state.isLoading = false
    },
    [getAccommodations.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getAccommodationStatistics.rejected.type]: (state) => {
      state.isLoading = false
    },
    [getAccommodationStatistics.pending.type]: (state) => {
      state.isLoading = true
    },
    [getAccommodationStatistics.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload: IGetAccommodationStatistics },
    ) => {
      state.statistics = payload
      state.isLoading = false
    },
    [getAccommodations.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getCamps.pending.type]: (state) => {
      state.isLoading = true
    },
    [getCamps.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllCampsSingleResponse> },
    ) => {
      state.camps = payload
      state.isLoading = false
    },
    [getCamps.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getCamps.pending.type]: (state) => {
      state.isLoading = true
    },
    [getBeds.pending.type]: (state) => {
      state.isLoading = true
    },
    [getBeds.fulfilled.type]: (
      state: IAccommodationSlice,
      {
        payload,
      }: {
        payload: IPaginatedResponse<IGetAllAvailableBedsSingleResponse> & { page?: number }
      },
    ) => {
      const page = payload.page ?? 1
      if (page === 1) {
        state.beds = {
          data: payload.data,
          totalCount: payload.totalCount,
          totalPages: payload.totalPages,
        }
      } else {
        state.beds.data = [...state.beds.data, ...payload.data]
        state.beds.totalCount = payload.totalCount
        state.beds.totalPages = payload.totalPages
      }
      state.isLoading = false
    },
    [getBeds.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getAccommodationUsers.pending.type]: (state) => {
      state.isLoading = true
    },
    [getAccommodationUsers.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllAccommodationUsersSingleResponse> },
    ) => {
      state.accomodationUsers = payload
      state.isLoading = false
    },
    [getAccommodationUsers.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getBedsHistory.pending.type]: (state) => {
      state.isLoading = true
    },
    [getBedsHistory.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload: IBedHistoryPaginationResponse<IGetAllBedsHiistorySingleResponse> },
    ) => {
      state.bedsHistory = payload
      state.isLoading = false
    },
    [getBedsHistory.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [checkIn.pending.type]: (state) => {
      state.isLoading = true
    },
    [checkIn.fulfilled.type]: (state: IAccommodationSlice) => {
      toast.success('Check-in successful')
      state.isLoading = false
    },
    [checkIn.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [checkOut.pending.type]: (state) => {
      state.isLoading = true
    },
    [checkOut.fulfilled.type]: (state: IAccommodationSlice) => {
      toast.success('Check-out successful')
      state.isLoading = false
    },
    [checkOut.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleAccommodationInfo.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleAccommodationInfo.rejected.type]: (state) => {
      state.isLoading = false
    },
    [getSingleAccommodationInfo.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload: IAccommodationInfoResponse },
    ) => {
      state.singleAccommodationInfo = payload
      state.isLoading = false
    },
    [getCheckouts.pending.type]: (state) => {
      state.isLoading = true
    },
    [getCheckouts.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllExpectedCheckoutsSingleResponse> },
    ) => {
      state.checkOuts = payload
      state.isLoading = false
    },
    [getCheckouts.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createCamp.pending.type]: (state) => {
      state.isLoading = true
    },
    [createCamp.fulfilled.type]: (state) => {
      state.isLoading = false
    },
    [createCamp.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editAccommodation.pending.type]: (state) => {
      state.isLoading = true
    },
    [editAccommodation.fulfilled.type]: () => {
      toast.success('Accommodation updated successfully')
    },
    [editAccommodation.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteAccommodation.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteAccommodation.fulfilled.type]: (state: IAccommodationSlice) => {
      toast.success('Accommodation deleted successfully')
      state.isLoading = false
    },
    [deleteAccommodation.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleAccommodation.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleAccommodation.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload },
    ) => {
      state.isLoading = false
    },
    [getSingleAccommodation.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearAccommodations, clearAccommodationInfo, clearAccommodationUsers, clearBeds } =
  accommodationSlice.actions
export default accommodationSlice.reducer
